import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import KnowledgeBaseD3Dialog from "../AgentCreate/KnowledgeBaseD3Dialog";
import { SHARED_KB_COPY } from "../../constants/sharedKnowledgeCopy";

const actionSx = { textTransform: "none", fontSize: "0.72rem", flexShrink: 0 };

/**
 * A share recipient's own Customer Data (D3) folders for an agent, as one line
 * of status plus the actions that apply. `controller` is the object returned by
 * `useMyDim3`; it renders nothing for anyone who is not a recipient.
 *
 * `variant="bar"` is the chat screen — the one place every recipient reaches,
 * read share or write share. `variant="card"` is the builder's D3 feature card.
 * `notice` replaces the "no folders chosen" line when a run has just reported
 * that it answered without the user's documents.
 */
export const MyDim3Status = ({ controller, variant = "bar", notice = "" }) => {
  const isCard = variant === "card";

  if (!controller?.isRecipient) {
    return isCard && controller?.loading ? <CircularProgress size={16} /> : null;
  }

  // The builder card supplies its own padded box; the chat bar sits in a stack.
  const wrapperSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 1,
    ...(isCard ? {} : { mt: 1 }),
  };

  // Shared-memory share: every recipient builds one common history, so it keeps
  // running on the owner's folders and takes no personal selection.
  if (controller.mode === "owner") {
    return (
      <Box sx={wrapperSx}>
        <Typography variant="caption" color="text.secondary">{SHARED_KB_COPY.ownerMode}</Typography>
      </Box>
    );
  }

  if (!controller.canSelect) {
    // The owner's agent does not use DIM3 — nothing to say about it in chat.
    if (!isCard) return null;
    return (
      <Box sx={wrapperSx}>
        <Typography variant="caption" color="text.secondary">{SHARED_KB_COPY.dim3Disabled}</Typography>
      </Box>
    );
  }

  if (!controller.selected) {
    return (
      <Box sx={wrapperSx}>
        <Typography
          variant="caption"
          color={notice ? "warning.main" : "text.secondary"}
          sx={{ flex: "1 1 180px", fontWeight: notice ? 600 : 400 }}
        >
          {notice || SHARED_KB_COPY.dim3None}
        </Typography>
        <Button size="small" variant="outlined" onClick={controller.openPicker} sx={actionSx}>
          {SHARED_KB_COPY.chooseFolders}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={wrapperSx}>
      <Tooltip title={SHARED_KB_COPY.dim3Info} arrow>
        <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 180px" }}>
          {SHARED_KB_COPY.dim3Selected(controller.folderCount)}
        </Typography>
      </Tooltip>
      <Button size="small" variant="outlined" onClick={controller.openPicker} sx={actionSx}>
        {SHARED_KB_COPY.changeFolders}
      </Button>
      <Tooltip title={SHARED_KB_COPY.removeHelp} arrow>
        <Button size="small" variant="text" color="inherit" onClick={controller.openRemove} sx={actionSx}>
          {SHARED_KB_COPY.removeFolders}
        </Button>
      </Tooltip>
    </Box>
  );
};

/**
 * The folder picker and the remove confirmation behind `MyDim3Status`. Render it
 * once, next to whichever component created the `useMyDim3` controller.
 */
export const MyDim3Dialog = ({ controller, agentName, onNotify }) => {
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  if (!controller?.isRecipient) return null;

  const handleSave = async (formData) => {
    await controller.save(formData);
    onNotify?.(SHARED_KB_COPY.saved, "success");
  };

  const closeRemove = () => {
    if (removing) return;
    setRemoveError("");
    controller.closeRemove();
  };

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError("");
    try {
      await controller.reset();
      controller.closeRemove();
      onNotify?.(SHARED_KB_COPY.removed, "success");
    } catch (error) {
      setRemoveError(error?.message || SHARED_KB_COPY.removeFailed);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <KnowledgeBaseD3Dialog
        personal
        open={controller.pickerOpen}
        onClose={controller.closePicker}
        onSave={handleSave}
        initialData={controller.selection}
      />

      <Dialog open={controller.removeOpen} onClose={closeRemove} maxWidth="xs" fullWidth>
        <DialogTitle>{SHARED_KB_COPY.removeConfirm(controller.folderCount, agentName)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">{SHARED_KB_COPY.removeHelp}</Typography>
          {removeError && <Alert severity="error" sx={{ mt: 1.5 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemove} disabled={removing}>Cancel</Button>
          <Button onClick={handleRemove} color="error" variant="contained" disabled={removing}>
            {removing ? "Removing…" : SHARED_KB_COPY.removeFolders}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
